import { useInvalidate, useNotification } from '@refinedev/core'
import type { Cabinet, IssuedCabinetKey } from '@sirius/contracts/admin'
import { useMutation } from '@tanstack/react-query'
import { TriangleAlertIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdminClient } from '~/api/adminClientContext'
import { ConfirmDialog } from '~/components/confirmDialog'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { RECORD_ACTIVITY_RESOURCE } from '~/providers/dataProvider'
import { errorMessage } from '~/providers/errors'
import { keyActionFor } from './cabinetKey'

function CopyField({
  label,
  copyLabel,
  value,
}: {
  label: string
  copyLabel: string
  value: string
}) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const { open } = useNotification()

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
    } catch {
      open?.({
        type: 'error',
        message: t('cabinetsPage.key.copyFailed'),
        description: t('cabinetsPage.key.copyFailedDescription'),
      })
    }
  }

  return (
    <div className="grid gap-1">
      <span className="text-muted-foreground text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <code
          aria-label={label}
          className="bg-muted flex-1 rounded px-2 py-1.5 font-mono text-sm break-all select-all"
        >
          {value}
        </code>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void copy()}
          aria-label={copyLabel}
        >
          {copied ? t('cabinetsPage.key.copied') : t('cabinetsPage.key.copy')}
        </Button>
      </div>
    </div>
  )
}

/** Shows a freshly issued key once. It cannot be fetched again after closing. */
function IssuedKeyDialog({
  cabinet,
  issued,
  onClose,
}: {
  cabinet: Cabinet
  issued: IssuedCabinetKey | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  return (
    <Dialog
      open={issued !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent
        // closing loses the key for good: only the explicit buttons close
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {t('cabinetsPage.key.dialogTitle', { name: cabinet.name })}
          </DialogTitle>
          <DialogDescription>
            {t('cabinetsPage.key.dialogDescription')}
          </DialogDescription>
        </DialogHeader>
        <p
          role="alert"
          className="border-destructive/50 text-destructive flex gap-2 rounded-md border p-3 text-sm"
        >
          <TriangleAlertIcon className="size-4 shrink-0" />
          <span>{t('cabinetsPage.key.onceWarning')}</span>
        </p>
        {issued && (
          <div className="grid gap-3">
            <CopyField
              label={t('cabinetsPage.key.cabinetId')}
              copyLabel={t('cabinetsPage.key.cabinetIdCopyAria')}
              value={cabinet.id}
            />
            <CopyField
              label={t('cabinetsPage.key.key')}
              copyLabel={t('cabinetsPage.key.keyCopyAria')}
              value={issued.key}
            />
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose}>{t('cabinetsPage.key.done')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Issue key / Rotate key via `cabinets.issueKey`, then the show-once dialog. */
export function CabinetKeyAction({ cabinet }: { cabinet: Cabinet }) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const invalidate = useInvalidate()
  const { open } = useNotification()
  const [issued, setIssued] = useState<IssuedCabinetKey | null>(null)
  const action = keyActionFor(cabinet, t)
  const { mutate, isPending } = useMutation({
    mutationFn: () => client.cabinets.issueKey({ id: cabinet.id }),
    onSuccess: async (result) => {
      setIssued(result)
      await Promise.all([
        invalidate({
          resource: 'cabinets',
          invalidates: ['list', 'detail'],
          id: cabinet.id,
        }),
        // the show page's History lists the new event
        invalidate({
          resource: RECORD_ACTIVITY_RESOURCE,
          invalidates: ['list'],
        }),
      ])
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('cabinetsPage.key.issueFailed'),
        description: errorMessage(error),
      })
    },
  })

  const dialog = (
    <IssuedKeyDialog
      cabinet={cabinet}
      issued={issued}
      onClose={() => setIssued(null)}
    />
  )

  if (action === null) {
    return dialog
  }

  const button = (
    <Button
      variant="outline"
      size="sm"
      disabled={isPending}
      onClick={action.rotate ? undefined : () => mutate()}
    >
      {action.label}
    </Button>
  )

  return (
    <>
      {action.rotate ? (
        <ConfirmDialog
          trigger={button}
          title={t('cabinetsPage.key.rotateTitle', { name: cabinet.name })}
          description={t('cabinetsPage.key.rotateDescription')}
          confirmLabel={t('cabinetsPage.key.rotate')}
          destructive
          onConfirm={() => mutate()}
        />
      ) : (
        button
      )}
      {dialog}
    </>
  )
}
